import React, { useEffect, useRef, useState } from 'react';
import UserRolesSection from '../roles/UserRolesSection';

/**
 * Sticky header inside the user-detail drawer.
 *
 * Replaces the previous wall of action buttons:
 *   - Status chips: Admin · Dept manager · X, Y / Employee / Inactive
 *   - One primary action only:
 *       active user   → "Deactivate" (red secondary link)
 *       inactive user → "Reactivate" (primary)
 *   - Everything else (Make/Remove Admin, Delete permanently, Manage roles)
 *     lives in a single kebab (⋯) menu.
 *
 * Props:
 *   user                       — selected user object (user.roles is the
 *                                 array of role keys currently held)
 *   onToggleAdmin              — () => void
 *   onDeactivate               — () => void
 *   onReactivate               — () => void
 *   onDeletePermanently        — () => void
 *   onRolesChanged             — () => void, called after a role is
 *                                 assigned/unassigned so the parent can
 *                                 refresh the selected user (defaults to a
 *                                 no-op if not provided by the parent)
 */
export default function UserDetailHeader({
  user,
  onToggleAdmin,
  onDeactivate,
  onReactivate,
  onDeletePermanently,
  onSyncOdoo,
  syncing = false,
  onRolesChanged,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [rolesPanelOpen, setRolesPanelOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Close the roles panel whenever the selected user changes so it doesn't
  // stay open (and stale) across drawer navigations.
  useEffect(() => {
    setRolesPanelOpen(false);
  }, [user?.id]);

  if (!user) return null;

  const isActive = user.is_active !== false;
  const isAdmin = !!user.is_admin;
  const isManager = (user.roles || []).includes('dept_manager');
  const managedNames = user.managed_dept_names || [];

  const handleMenu = (fn) => () => {
    setMenuOpen(false);
    fn?.();
  };

  const showDeactivate = isActive && !!onDeactivate;
  const showReactivate = !isActive && !!onReactivate;
  const showManageRoles = !!onRolesChanged;
  const showToggleAdmin = isActive && !!onToggleAdmin;
  const showDeletePermanently = !isActive && !!onDeletePermanently;
  const showKebab = !!onSyncOdoo || showManageRoles || showToggleAdmin || showDeletePermanently;

  return (
    <div className="ut-dh">
      <div className="ut-dh-chips" aria-label="Roles and status">
        {isAdmin && <span className="ut-chip ut-chip--admin">Admin</span>}
        {isManager && (
          <span
            className="ut-chip ut-chip--manager"
            data-tooltip={managedNames.length ? `Manages: ${managedNames.join(', ')}` : 'Department manager'}
            title={managedNames.length ? `Manages: ${managedNames.join(', ')}` : 'Department manager'}
            tabIndex={0}
            aria-label={managedNames.length ? `Manager of ${managedNames.join(', ')}` : 'Department manager'}
          >
            {managedNames.length === 0 && 'Manager'}
            {managedNames.length === 1 && `Manager · ${managedNames[0]}`}
            {managedNames.length === 2 && `Manager · ${managedNames.join(', ')}`}
            {managedNames.length > 2 && `Manager · ${managedNames.length} departments`}
          </span>
        )}
        {!isAdmin && !isManager && isActive && (
          <span className="ut-chip ut-chip--employee">Employee</span>
        )}
        {!isActive && <span className="ut-chip ut-chip--inactive">Inactive</span>}
      </div>

      <div className="ut-dh-actions">
        {showDeactivate && (
          <button type="button" className="ut-dh-primary ut-dh-primary--danger" onClick={onDeactivate}>
            Deactivate
          </button>
        )}
        {showReactivate && (
          <button type="button" className="ut-dh-primary" onClick={onReactivate}>
            Reactivate
          </button>
        )}
        {showKebab && (
          <div className="ut-dh-menu-wrap" ref={menuRef}>
            <button
              type="button"
              className="ut-dh-kebab"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="More actions"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
            </button>
            {menuOpen && (
              <ul className="ut-dh-menu" role="menu">
                {onSyncOdoo && (
                  <li role="none">
                    <button type="button" role="menuitem" disabled={syncing} onClick={handleMenu(onSyncOdoo)}>
                      {syncing ? 'Syncing…' : 'Sync from Odoo'}
                    </button>
                  </li>
                )}
                {showManageRoles && (
                  <li role="none">
                    <button type="button" role="menuitem" onClick={handleMenu(() => setRolesPanelOpen((o) => !o))}>
                      Manage roles
                    </button>
                  </li>
                )}
                {showToggleAdmin && (
                  <li role="none">
                    <button type="button" role="menuitem" onClick={handleMenu(onToggleAdmin)}>
                      {isAdmin ? 'Remove admin' : 'Make admin'}
                    </button>
                  </li>
                )}
                {showDeletePermanently && (
                  <li role="none">
                    <button type="button" role="menuitem" className="is-danger" onClick={handleMenu(onDeletePermanently)}>
                      Delete permanently…
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>

      {showManageRoles && rolesPanelOpen && (
        <div className="ut-dh-roles-panel">
          <div className="ut-dh-roles-panel-head">
            <h4>Manage roles</h4>
            <button
              type="button"
              className="ut-dh-roles-panel-close"
              aria-label="Close manage roles panel"
              onClick={() => setRolesPanelOpen(false)}
            >
              ×
            </button>
          </div>
          <UserRolesSection
            userId={user.id}
            currentRoleKeys={user.roles || []}
            onChanged={() => onRolesChanged?.()}
          />
        </div>
      )}
    </div>
  );
}
